import React, { useEffect, useMemo, useState } from "react";
import { Receipt, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, RowSearch } from "@/pages/shared";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: today(), description: "", supplier: "", amount: "", vat_rate: 21 });

export default function AdminExpenses() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try { setRows((await api.get("/expenses")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const base = Number(form.amount) || 0;
  const vat = Math.round(base * (Number(form.vat_rate) || 0)) / 100;

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/expenses", { ...form, amount: base, vat_rate: Number(form.vat_rate) || 0 });
      setOpen(false); setForm(blank()); await load();
      toast.success(t("created"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.description, r.supplier, r.date].some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);
  const totals = useMemo(() => visible.reduce((a, r) => ({ base: a.base + r.amount, vat: a.vat + r.vat_amount }), { base: 0, vat: 0 }), [visible]);

  return (
    <Panel
      title={`${t("admin_expenses")} (${visible.length})`}
      testid="expenses-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("search_placeholder")} testid="expenses-search" />
          <Button onClick={() => { setForm(blank()); setOpen(true); }} data-testid="add-expense-button"><Plus className="h-4 w-4 mr-1" />{t("expense_new")}</Button>
        </div>
      }
    >
      <p className="px-5 pt-4 text-xs text-muted-foreground" data-testid="expenses-hint">{t("expenses_hint")}</p>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("description")}</TableHead>
              <TableHead>{t("sup_name")}</TableHead>
              <TableHead className="text-right">{t("excl_vat")}</TableHead>
              <TableHead className="text-right">{t("btw")} %</TableHead>
              <TableHead className="text-right">{t("btw")}</TableHead>
              <TableHead className="text-right">{t("incl_vat")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell className="text-muted-foreground text-xs">{fmtDate(r.date)}</TableCell>
                <TableCell className="font-semibold text-foreground">{r.description}</TableCell>
                <TableCell className="text-sm">{r.supplier || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.amount)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.vat_rate}%</TableCell>
                <TableCell className="text-right tabular-nums text-slate-500">{money(r.vat_amount)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(r.total)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`delete-expense-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/30 font-bold">
              <TableCell colSpan={3}>{t("total")}</TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.base)}</TableCell>
              <TableCell></TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.vat)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.base + totals.vat)}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(blank()); }}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Receipt className="h-4 w-4" />{t("expense_new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t("date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("sup_name")}</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Energieleverancier…" data-testid="expense-supplier" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs">{t("description")}</Label><Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("expense_desc_ph")} data-testid="expense-description" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("excl_vat")}</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("btw")} %</Label><Input type="number" step="1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="expense-vat" /></div>
            <div className="col-span-2 flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t("btw")}: <span className="font-semibold text-foreground tabular-nums" data-testid="expense-vat-amount">{money(vat)}</span></span>
              <span className="font-display font-extrabold text-primary tabular-nums" data-testid="expense-total">{money(base + vat)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={busy || !form.description.trim() || !base} data-testid="save-expense-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
